/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 *  author : vhe
 *  date : 02/03/2021 07:39
 */

import { ModelProperty } from '../engine/model-property';
import { PropertyType } from '../engine/property-type.enum';
import { Entity } from '../model/entity/entity';
import { DataModel } from '../engine/data-model';
import { StandardEntity } from '../engine/standard-entity';

export class ECalculator {
  private total: any;
  /**
   * calculate the total of an array of entity
   */
  sum(model: DataModel, group: any[]): any {
    this.total = new StandardEntity();
    for (const elem of model.properties) {
      if (elem.showOnTotal) {
        let total = this.compute(elem, group, '+');
        if (total && typeof total === 'number' && elem.decimal !== null) {
          total = total.toFixed(elem.decimal);
        }
        this.total[elem.id] = total;
      }
    }
    return this.total;
  }

  /**
   * distribute the way to calculate by type
   * @param elem: property
   * @param group
   * @param operator
   */
  private compute(elem: ModelProperty, group: Entity[], operator: '+' | '-') {
    let total: any;
    switch (elem.editType) {
      case PropertyType.number:
        total = this.computeNumber(elem, group, operator);
        break;
      case PropertyType.string:
        total = this.computeString(elem, group, operator);
        break;
      case PropertyType.time:
      case PropertyType.duration:
        total = this.computeTime(elem, group, operator);
        break;
      default:
        total = ' - ';
    }
    return total;
  }

  /**
   * calculate operation for the type "number" of property
   * @param elem
   * @param group
   * @param operator
   */
  private computeNumber(
    elem: ModelProperty,
    group: Entity[],
    operator: '+' | '-',
  ): number {
    let total = 0;
    for (const entity of group) {
      let value = entity[elem.id];
      if (typeof entity[elem.id] === 'string') {
        value = parseFloat(entity[elem.id].replace(',', '.'));
      }
      if (operator === '+') {
        total += value;
      }
      if (operator === '-') {
        total -= value;
      }
    }
    return Number(total);
  }
  /**
   * calculate operation for the type "number" of property
   * @param elem
   * @param group
   * @param operator
   */
  private computeString(
    elem: ModelProperty,
    group: any[],
    operator: '+' | '-',
  ): string {
    let total = 0;
    for (const entity of group) {
      const num = isNaN(Number(entity[elem.id])) ? 0 : Number(entity[elem.id]);
      if (operator === '+') {
        total += num;
      }
      if (operator === '-') {
        total -= num;
      }
    }
    return total.toFixed(2);
  }

  /**
   * calculate operation for the type "time" of property
   * @param elem
   * @param group
   * @param operator
   */
  private computeTime(
    elem: ModelProperty,
    group: any[],
    operator: '+' | '-',
  ): string {
    const sign = operator === '+' ? 1 : -1;
    let minutes = 0;
    for (const entity of group) {
      if (entity[elem.id]) {
        minutes += sign * this.toMinutes(entity[elem.id]);
      }
    }
    return this.formatMinutes(minutes);
  }

  /**
   * convert a "[-]HH:mm" value to signed minutes, 0 if the value is invalid
   * @param value
   */
  private toMinutes(value: string): number {
    // The sign applies to the whole value ('-01:15' = -75 min), not only to the hours
    const negative = value.startsWith('-');
    const [hours, mins] = value.replace(/^-/, '').split(':');
    const total = parseInt(hours, 10) * 60 + parseInt(mins, 10);
    if (isNaN(total)) {
      return 0;
    }
    return negative ? -total : total;
  }

  /**
   * format signed minutes as "[-]HH:mm", hours on at least two digits
   * @param minutes
   */
  private formatMinutes(minutes: number): string {
    const abs = Math.abs(minutes);
    const hours = String(Math.floor(abs / 60)).padStart(2, '0');
    const mins = String(abs % 60).padStart(2, '0');
    return `${minutes < 0 ? '-' : ''}${hours}:${mins}`;
  }
}
